import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useEffect, useState} from "react";
import {useHistory} from "react-router";
import {useLocation} from "react-router-dom";

import {AccordionSection} from "@khanacademy/wonder-blocks-accordion";
import {View} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Log from "@khan/logging";
import {mediaQueries} from "@khan/shared-styles";

import {
    getValidContentKinds,
    getValidTopicAncestors,
    type FilterLabel,
} from "./indexed-search/constants.ts";
import Filters from "./indexed-search/filters.tsx";
import IndexedSearchResults from "./indexed-search/results.tsx";
import {IsArraysContentsEqual} from "./indexed-search/utils.ts";

type Props = {
    /** The query to search for. */
    query: string;
    /** Whether the Google-powered search is enabled by default. */
    googlePoweredDefaultState: boolean;
    /** A function to toggle the Google-powered search. */
    googlePoweredToggleFunc: () => void;
};

const IndexedSearch = ({
    query,
    googlePoweredDefaultState,
    googlePoweredToggleFunc,
}: Props) => {
    const location = useLocation();
    const history = useHistory();
    const updateHistory = (newSearch: string) => {
        Log.log("updating history!", null, newSearch);
        history.push({
            pathname: location.pathname,
            search: newSearch,
        });
    };

    // Get initial state from URL parameters.
    const searchParams = new URLSearchParams(location.search);

    const initialPageNum = parseInt(searchParams.get("page") || "1", 10);
    const initialContentKinds =
        searchParams.get("content_kinds")?.split(",") || [];
    const initialDomains = searchParams.get("domains")?.split(",") || [];

    // State.
    const [collapsed, setCollapsed] = useState(true);
    const [pageNum, setPageNum] = useState(initialPageNum);
    const [selectedContentKinds, setSelectedContentKinds] =
        useState<string[]>(initialContentKinds);
    const [selectedDomains, setSelectedDomains] =
        useState<string[]>(initialDomains);

    // Update state from URL parameters.
    useEffect(() => {
        Log.log("location changed.", null, location);

        updateStateFromParam(
            pageNum,
            "page",
            (value) => value === 1,
            1,
            setPageNum,
            (value) => parseInt(value, 10),
            (value, paramValue) => value === paramValue,
        );

        updateStateFromParam(
            selectedContentKinds,
            "content_kinds",
            (value) => value.length === 0,
            [],
            setSelectedContentKinds,
            (value) => value.split(","),
            IsArraysContentsEqual,
        );

        updateStateFromParam(
            selectedDomains,
            "domains",
            (value) => value.length === 0,
            [],
            setSelectedDomains,
            (value) => value.split(","),
            IsArraysContentsEqual,
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    // Update URL parameters when state changes.
    useEffect(() => {
        Log.log(
            "page, content kinds, or domains changed",
            null,
            pageNum,
            selectedContentKinds,
            selectedDomains,
        );

        let currentParams = new URLSearchParams(location.search);

        let pageUpdated = false;
        let contentKindsUpdated = false;
        let domainsUpdated = false;

        [currentParams, pageUpdated] = updateQueryParameter(
            pageNum,
            "page",
            currentParams,
            (value) => parseInt(value, 10),
            (value, paramValue) => value === paramValue,
            (value) => value === 1,
        );

        [currentParams, contentKindsUpdated] = updateQueryParameter(
            selectedContentKinds,
            "content_kinds",
            currentParams,
            (value) => value.split(","),
            IsArraysContentsEqual,
            (value) => value.length === 0,
        );

        [currentParams, domainsUpdated] = updateQueryParameter(
            selectedDomains,
            "domains",
            currentParams,
            (value) => value.split(","),
            IsArraysContentsEqual,
            (value) => value.length === 0,
        );

        if (pageUpdated || contentKindsUpdated || domainsUpdated) {
            updateHistory(currentParams.toString());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNum, selectedContentKinds, selectedDomains]);

    const AccordionHeader = (
        <View style={styles.accordionHeader}>{t`Refine your search`}</View>
    );

    return (
        <View style={styles.container}>
            <View style={styles.navColumn}>
                <View style={styles.navColumnContentSmall}>
                    <AccordionSection
                        tag="h2"
                        header={AccordionHeader}
                        expanded={!collapsed}
                        onToggle={() => setCollapsed(!collapsed)}
                    >
                        <Filters
                            identifier="mobile"
                            contentKinds={selectedContentKinds}
                            setContentKinds={setSelectedContentKinds}
                            domains={selectedDomains}
                            setDomains={setSelectedDomains}
                            setPageNumber={setPageNum}
                        />
                    </AccordionSection>
                </View>
                <View style={styles.navColumnContentLarge}>
                    <aside>
                        <Filters
                            identifier="desktop"
                            contentKinds={selectedContentKinds}
                            setContentKinds={setSelectedContentKinds}
                            domains={selectedDomains}
                            setDomains={setSelectedDomains}
                            setPageNumber={setPageNum}
                        />
                    </aside>
                </View>
            </View>
            <View style={[styles.mainColumn]}>
                <IndexedSearchResults
                    query={query}
                    contentKinds={
                        selectedContentKinds.length
                            ? selectedContentKinds
                            : getValidContentKinds().map(
                                  (kind: FilterLabel) => kind.id,
                              )
                    }
                    allowedTopicAncestors={
                        selectedDomains.length
                            ? selectedDomains
                            : getValidTopicAncestors().map(
                                  ({id}: FilterLabel) => id,
                              )
                    }
                    googlePoweredDefaultState={googlePoweredDefaultState}
                    googlePoweredToggleFunc={googlePoweredToggleFunc}
                    pageNum={pageNum}
                    setPageNum={setPageNum}
                />
            </View>
        </View>
    );
};

const updateQueryParameter = (
    value: any,
    paramName: string,
    params: URLSearchParams,
    stringToValueType: (value: string) => any,
    valueEqualsParamValue: (value: any, paramValue: any) => boolean,
    isDefaultCondition: (value: any) => boolean,
): [URLSearchParams, boolean] => {
    // Copy params so we don't mutate the original.
    params = new URLSearchParams(params);

    const param = params.get(paramName);

    // If the value represents the default conditions (first page, or no filter selected, for example),
    // we don't need to add a query parameter. And if one exists, we should remove it.
    if (isDefaultCondition(value)) {
        if (param !== null) {
            params.delete(paramName);
            return [params, true];
        }
        return [params, false];
    }

    // Otherwise, we need to add a parameter, if one doesn't exist.
    if (param === null) {
        params.set(paramName, value.toString());
        return [params, true];
    }

    // If one does exist, we should update it if it's different from the
    // current value.
    const valueFromParam = stringToValueType(param);
    if (!valueEqualsParamValue(value, valueFromParam)) {
        params.set(paramName, value.toString());
        return [params, true];
    }

    return [params, false];
};

const updateStateFromParam = (
    value: any,
    paramName: string,
    isValueEmpty: (value: any) => boolean,
    emptyValue: any,
    stateSetter: (value: any) => void,
    paramValueToStateValue: (value: string) => any,
    valueEqualsParamValue: (value: any, paramValue: any) => boolean,
) => {
    const params = new URLSearchParams(location.search);
    const param = params.get(paramName);

    // If we do not have a parameter, we should set the state to empty. If it isn't already.
    if (param === null) {
        if (!isValueEmpty(value)) {
            Log.log(`Updating ${paramName} state to empty`);
            stateSetter(emptyValue);
        }
        return;
    }

    // If we have a parameter, we should set the state to that, if it isn't already.
    const stateValue = paramValueToStateValue(param);
    if (!valueEqualsParamValue(value, stateValue)) {
        Log.log(`Updating ${paramName} state`, null, stateValue);
        stateSetter(stateValue);
    }
};

const styles = StyleSheet.create({
    container: {
        width: "100%",
        color: color.offBlack,
        marginTop: spacing.small_12,

        // Tablet & desktop view: two-column layout
        [mediaQueries.smOrLarger]: {
            flexDirection: "row",
        },

        // Mobile view: vertical stacking
        [mediaQueries.xsOrSmaller]: {
            flexDirection: "column",
            paddingLeft: 0,
            paddingRight: 0,
        },
    },
    mainColumn: {
        flex: "1",
        background: color.white,

        // Tablet & desktop view: two-column layout
        [mediaQueries.mdOrLarger]: {
            paddingTop: spacing.medium_16,
            paddingBottom: spacing.medium_16,
            paddingLeft: spacing.xLarge_32,
            paddingRight: spacing.xLarge_32,
        },

        // Mobile view: vertical stacking
        [mediaQueries.smOrSmaller]: {
            paddingTop: spacing.small_12,
            paddingBottom: spacing.small_12,
            paddingLeft: 0,
            paddingRight: 0,
        },

        "@media print": {
            paddingLeft: spacing.large_24,
            paddingRight: spacing.large_24,
        },
    },
    navColumn: {
        width: 230,
        minHeight: "100vh",
        boxSizing: "border-box",
        flex: "0 0 auto",
        background: color.white,
        borderRight: `1px solid ${color.offBlack16}`,

        // Mobile view: vertical stacking
        [mediaQueries.xsOrSmaller]: {
            width: "100%",
            minHeight: 0,
            borderRight: 0,

            paddingTop: spacing.small_12,
            paddingBottom: spacing.small_12,
        },

        "@media print": {
            display: "none",
        },
    },
    navColumnContentSmall: {
        display: "none",
        // Tablet & desktop view: two-column layout
        [mediaQueries.xsOrSmaller]: {
            display: "block",
        },
    },
    navColumnContentLarge: {
        display: "none",
        // Tablet & desktop view: two-column layout
        [mediaQueries.smOrLarger]: {
            display: "block",
        },
    },
    accordionHeader: {
        paddingTop: spacing.xxSmall_6,
        paddingBottom: spacing.xxSmall_6,
        paddingLeft: spacing.medium_16,
    },
});

export default IndexedSearch;
